import React, { useEffect, useState, useCallback } from "react";
import {
  Select,
  SelectProps,
  NonCancelableCustomEvent,
} from "@amzn/awsui-components-react-v3";
import { PayloadAction } from "redux-starter-kit";
import { useHttpRequest, LoadingState } from "../../http";
import { actions } from "../formSlice";
import { MetricsState } from "../../logging";
import PropertyResponseDto from "../PropertyResponseDto";
import { FilterDto } from "../../common/model/filter.dto";

const buildUrl = (propertyName: string): string =>
  `/api/v1/properties/${propertyName}`;

const OPERATION_NAME = "getFormFields";

function transformLoadingStateToStatusType(
  loadingState?: LoadingState
): "pending" | "loading" | "error" | "finished" | undefined {
  switch (loadingState) {
    case LoadingState.NO_DATA:
      return "pending";
    case LoadingState.LOADING:
      return "loading";
    case LoadingState.REJECTED:
      return "error";
    case LoadingState.RESOLVED:
      return "finished";
    default:
      return undefined;
  }
}

interface FormFieldSelectWrapperProps extends SelectProps {
  dispatch: (action: PayloadAction) => void;
  propertyName: string;
  value?: string | number;
  isNumber?: boolean;
  metricsState: MetricsState;
  dropdownOptionValues: (string | number)[];
  supportFetch?: boolean;
  dataFilters?: FilterDto[];
}

const FormFieldSelectWrapper: React.FC<FormFieldSelectWrapperProps> = ({
  propertyName,
  value,
  dispatch,
  isNumber,
  metricsState,
  dropdownOptionValues,
  supportFetch = false,
  disabled = false,
  invalid = false,
  placeholder,
  loadingText,
  errorText,
  recoveryText,
  finishedText,
  filteringType = "none",
  filteringPlaceholder,
  dataFilters,
}): JSX.Element => {
  const [hasBeenFocused, setHasBeenFocused] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string | undefined>(undefined);

  const selectedOption = value
    ? {
        id: value.toString(),
        label: value.toString(),
      }
    : null;

  const { loadingState, data: propertyData, sendRequest } = useHttpRequest<
    PropertyResponseDto
  >(OPERATION_NAME, metricsState);

  const sendRequestWrapper = useCallback(() => {
    const method = dataFilters && dataFilters.length > 0 ? "POST" : "GET";
    const data =
      dataFilters && dataFilters.length > 0
        ? { filters: dataFilters }
        : undefined;
    sendRequest({
      method,
      url: buildUrl(propertyName),
      data,
    });
  }, [dataFilters, propertyName, sendRequest]);

  const activeOptionValues =
    loadingState === LoadingState.RESOLVED && propertyData
      ? propertyData.allowedValues || []
      : dropdownOptionValues;

  const options = activeOptionValues.map((v: string | number) => ({
    id: v.toString(),
    label: v.toString(),
  }));

  const activeLoadingState = supportFetch ? loadingState : undefined;

  useEffect(() => {
    if (supportFetch && filteringType !== "manual" && hasBeenFocused) {
      sendRequestWrapper();
    }
  }, [
    dataFilters,
    hasBeenFocused,
    supportFetch,
    filteringType,
    sendRequestWrapper,
  ]);

  const onFocus = (): void => {
    setHasBeenFocused(true);
  };

  const onChange = (
    event: NonCancelableCustomEvent<{
      selectedOption: { label?: string; value?: string };
    }>
  ): void => {
    const label = event.detail.selectedOption.label || "";
    const parsedValue = !isNumber ? label : parseInt(label, 10);

    dispatch(
      actions.setPropertyValueAction({
        propertyName,
        value: parsedValue,
      })
    );
  };

  const onRecoveryClick = (): void => {
    sendRequestWrapper();
  };

  const selectFilteringType =
    filteringType === "auto" ? "manual" : filteringType;
  const selectOptions =
    filteringType === "auto" && filterText
      ? options.filter(({ id }) =>
          id.toLowerCase().startsWith(filterText.toLowerCase())
        )
      : options;
  return (
    <Select
      disabled={disabled}
      invalid={invalid}
      options={selectOptions}
      placeholder={placeholder}
      statusType={transformLoadingStateToStatusType(activeLoadingState)}
      selectedOption={selectedOption}
      selectedAriaLabel="Selected"
      onChange={onChange}
      onFocus={onFocus}
      loadingText={loadingText || "Loading"}
      errorText={errorText || "Error fetching results."}
      recoveryText={recoveryText || "Retry"}
      empty={filterText ? "We can’t find a match" : "No options"}
      noMatch="We can’t find a match"
      finishedText={finishedText}
      filteringType={selectFilteringType}
      filteringPlaceholder={filteringPlaceholder}
    ></Select>
  );
};

export default FormFieldSelectWrapper;
